import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Checkbox,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextareaAutosize,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon
} from '@mui/icons-material';

const PostmanDoc = ({ endpoint }) => {
  const theme = useTheme();
  const [method, setMethod] = useState(endpoint.methods?.[0] || 'GET');
  const [headers, setHeaders] = useState([
    { key: 'Content-Type', value: 'application/json', enabled: true },
    { key: 'Authorization', value: 'Bearer your_token_here', enabled: true },
    { key: 'tenant-code', value: 'vizionsys', enabled: true }
  ]);
  const [body, setBody] = useState(
    typeof endpoint.requestBody === 'string' 
      ? endpoint.requestBody 
      : JSON.stringify(endpoint.requestBody, null, 2)
  );
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('documentation');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const generateCurl = () => {
    let curl = `curl -X ${method} "${endpoint.path}" \\\n`;
    headers.filter(h => h.enabled).forEach(h => {
      curl += `  -H "${h.key}: ${h.value}" \\\n`;
    });
    if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
      curl += `  -d '${body.replace(/\n/g, '\\n')}'`;
    }
    return curl;
  };

  const handleSend = (type = 'success') => {
    setLoading(true);
    setResponse(null);
    
    setTimeout(() => {
      setResponse(endpoint.responses[type]);
      setLoading(false);
    }, 1000);
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const toggleHeader = (index) => {
    const newHeaders = [...headers];
    newHeaders[index].enabled = !newHeaders[index].enabled;
    setHeaders(newHeaders);
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '', enabled: true }]);
  };

  const removeHeader = (index) => {
    const newHeaders = [...headers];
    newHeaders.splice(index, 1);
    setHeaders(newHeaders);
  };

  const handleCopy = () => {
    setCopied(true);
    setSnackbarOpen(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return theme.palette.info.main;
      case 'POST': return theme.palette.success.main;
      case 'PUT': return theme.palette.warning.main;
      case 'DELETE': return theme.palette.error.main;
      default: return theme.palette.secondary.main;
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ 
      maxWidth: '35vw',
      p:1,
      bgcolor: 'background.default'
    }}>
      {/* Header */}
      <Box >
        <Typography  gutterBottom sx={{ color: 'text.primary' }}>
          {endpoint.name}
        </Typography>
        <Typography  color="text.secondary">
          {endpoint.description}
        </Typography>
      </Box>

      {/* Tabs */}
     <Tabs 
  value={activeTab} 
  onChange={(e, newValue) => setActiveTab(newValue)}
  sx={{ 
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main,
      // height: 3
    }
  }}
>
  <Tab 
    label="Documentation" 
    value="documentation" 
    sx={{
      color: activeTab === 'documentation' ? 
      '#3DDBDBFF': '#8AABC5FF'
        ,
      fontWeight: activeTab === 'documentation' ? 600 : 400,
      textTransform: 'none',
      fontSize: '0.875rem',
      minWidth: 'unset',
      '&:hover': {
        color: theme.palette.primary.main,
        opacity: 1
      },
      '&.Mui-selected': {
        color: theme.palette.primary.main
      }
    }}
  />
  <Tab 
    label="Test API" 
    value="testing" 
    sx={{
      color: activeTab === 'testing' ? 
        theme.palette.primary.main : 
        theme.palette.text.secondary,
      fontWeight: activeTab === 'testing' ? 600 : 400,
      textTransform: 'none',
      fontSize: '0.875rem',
      minWidth: 'unset',
      // padding: '6px 16px',
      '&:hover': {
        color: theme.palette.primary.main,
        opacity: 1
      },
      '&.Mui-selected': {
        color: theme.palette.primary.main
      }
    }}
  />
</Tabs>
---=-=
      {activeTab === 'documentation' ? (
        <Box>
          {/* Endpoint Details */}
          <Paper elevation={1} sx={{ 
            p: 1,
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 'bold',
                fontSize: 12,
                mr: 2,
                textTransform: 'uppercase',
                bgcolor: getMethodColor(method),
                color: 'white'
              }}>
                {method}
              </Box>
              <Typography 
                variant="body1" 
                fontFamily="monospace" 
                sx={{ 
                  flex: 1,
                  color: 'text.primary'
                }}
              >
                {endpoint.path}
              </Typography>
              <CopyToClipboard text={generateCurl()} onCopy={handleCopy}>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<CopyIcon />}
                  sx={{ 
                   
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  Copy cURL
                </Button>
              </CopyToClipboard>
            </Box>

            {/* Parameters */}
            {endpoint.parameters && endpoint.parameters.length > 0 && (
              <Box >
                <Typography  gutterBottom sx={{ color: 'text.primary' }}>
                  Parameters
                </Typography>
                <Table sx={{ 
                  border: '1px solid', 
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.800' }}>
                      <TableCell sx={{ width: '150px', fontWeight: 'bold', color: 'text.primary' }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ width: '100px', fontWeight: 'bold', color: 'text.primary' }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ width: '80px', fontWeight: 'bold', color: 'text.primary' }}>
                        Required
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {endpoint.parameters.map((param, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                          {param.name}
                        </TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{param.description}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{param.type}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>
                          {param.required ? 'Yes' : 'No'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Request Body */}
            {endpoint.requestBody && (
              <Box >
                <Typography gutterBottom sx={{ color: 'text.primary' }}>
                  Request Body
                </Typography>
                <Paper variant="outlined" sx={{ 
                 p: 1, 
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap', 
                    fontFamily: 'monospace', 
                    fontSize: '14px',
                    color: theme.palette.text.primary
                  }}>
                    {typeof endpoint.requestBody === 'string' 
                      ? endpoint.requestBody 
                      : JSON.stringify(endpoint.requestBody, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}

            {/* Response Examples */}
            {endpoint.responses && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                  Response Examples
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {Object.keys(endpoint.responses).map((type) => (
                    <Button
                      key={type}
                      variant="outlined"
                      onClick={() => handleSend(type)}
                      sx={{ 
                        textTransform: 'capitalize',
                        color: 'text.secondary',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      {type} ({endpoint.responses[type]?.status})
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* Response Display */}
            {response && (
              <Box>
                <Typography  gutterBottom sx={{ color: 'text.primary' }}>
                  Response
                </Typography>
                <Alert severity={getStatusColor(response.status)} sx={{ mb: 2 }}>
                  Status: {response.status}
                </Alert>
                <Paper variant="outlined" sx={{ 
                 
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap', 
                    fontFamily: 'monospace', 
                    fontSize: '14px',
                    color: theme.palette.text.primary
                  }}>
                    {JSON.stringify(response.data || response, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        <Box>
          {/* Testing Interface */}
          <Paper elevation={1} sx={{ 
            p: 1, 
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', mb: 3 }}>
              <FormControl sx={{ minWidth: 100, mr: 0 }}>
                <Select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  variant="outlined"
                  sx={{
                    bgcolor: 'background.paper',
                    color: 'text.primary'
                  }}
                >
                  {endpoint.methods.map(m => (
                    <MenuItem 
                      key={m} 
                      value={m}
                      sx={{ color: 'text.primary' }}
                    >
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                value={endpoint.path}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                    color: 'text.primary'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 'none'
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSend('success')}
                disabled={loading}
                startIcon={<SendIcon />}
                sx={{ 
                  ml: 1,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0
                }}
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            </Box>

            {/* Headers Editor */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Headers
              </Typography>
              <Table sx={{ 
                border: '1px solid', 
                borderColor: 'divider',
                bgcolor: 'background.paper'
              }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.800' }}>
                    <TableCell sx={{ width: '50px', color: 'text.primary' }}>Enabled</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>Key</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          checked={header.enabled}
                          onChange={() => toggleHeader(index)}
                          sx={{ color: 'primary.main' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={header.key}
                          onChange={(e) => updateHeader(index, 'key', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'text.primary',
                              '& fieldset': {
                                borderColor: 'divider'
                              }
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={header.value}
                          onChange={(e) => updateHeader(index, 'value', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'text.primary',
                              '& fieldset': {
                                borderColor: 'divider'
                              }
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Request Body Editor */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Request Body
              </Typography>
              <TextareaAutosize
                minRows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Request body (JSON)"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  resize: 'none',
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary
                }}
              />
            </Box>
          </Paper>

          {/* Response Display */}
          <Paper elevation={1} sx={{ 
            p: 1,
            m:1,
            bgcolor: 'background.paper'
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
              Response
            </Typography>
            
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexDirection: 'column',
                height: 200 
              }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography sx={{ color: 'text.secondary' }}>
                  Sending request...
                </Typography>
              </Box>
            ) : response ? (
              <Box>
                <Alert severity={getStatusColor(response.status)} >
                  Status: {response.status}
                </Alert>
                <Paper variant="outlined" sx={{ 
                  p: 1, 
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    whiteSpace: 'pre-wrap', 
                    fontFamily: 'monospace', 
                    fontSize: '14px',
                    color: theme.palette.text.primary
                  }}>
                    {JSON.stringify(response.data || response, null, 2)}
                  </pre>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                // height: 200 ,
                
              }}>
                <Typography color="text.secondary">
                  Send a request to see the response here
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Snackbar for copy notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="success" 
          sx={{ 
            width: '100%',
            bgcolor: 'success.main',
            color: 'white'
          }}
        >
          cURL command copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostmanDoc;